import React, { useEffect, useState } from "react";
import { Bar, Doughnut, Line } from "react-chartjs-2";
import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale, ArcElement, LineElement, PointElement } from "chart.js"; // Added PointElement
import { getDatabase, ref, get } from "firebase/database";
import { getAuth, onAuthStateChanged } from "firebase/auth"; // Import authentication functions
import app from "../firebase-config";
import { useNavigate } from "react-router-dom";

// Register necessary chart components
ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale, ArcElement, LineElement, PointElement); // Registered PointElement

function Dashboard() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [quantities, setQuantities] = useState([]);
  const [lowStockCategories, setLowStockCategories] = useState([]);
  const [username, setUsername] = useState(""); // State to hold the username
  const [salesHistory, setSalesHistory] = useState([]); // State for sales history
  
  const lowStockThreshold = 10; // Define the threshold for low stock

  // Fetch items data from Firebase
  const fetchData = async () => {
    const db = getDatabase(app);
    const dbRef = ref(db, "beverages/coffee");
    const snapshot = await get(dbRef);
    if (snapshot.exists()) {
      const myData = snapshot.val();
      const categoryCount = {};
      const lowStockItems = []; // Track items with low stock, including their category
  
      // Loop through the data and group by category
      Object.keys(myData).forEach((key) => {
        const item = myData[key];
        const category = item.category || "Uncategorized"; // Default to Uncategorized if no category is found
        const quantity = parseInt(item.quantity, 10);
  
        if (categoryCount[category]) {
          categoryCount[category] += quantity;
        } else {
          categoryCount[category] = quantity;
        }
  
        // Check if the item's stock is below the threshold
        if (quantity < lowStockThreshold) {
          lowStockItems.push({ itemName: item.coffeeName, category, quantity });
        }
      });
  
      // Set data for charts and low stock warnings
      setCategories(Object.keys(categoryCount));
      setQuantities(Object.values(categoryCount));
      setLowStockCategories(lowStockItems);
    } else {
      console.error("No data available");
    }
  };

  // Fetch sales history data from Firebase
  const fetchSalesHistory = async () => {
    const db = getDatabase(app);
    const salesRef = ref(db, "sales");
    const snapshot = await get(salesRef);
    if (snapshot.exists()) {
      const salesData = snapshot.val();
      const salesByDate = {}; // Will store total sales by date

      // Loop through the sales data and group by date
      Object.keys(salesData).forEach((key) => {
        const sale = salesData[key];
        const saleDate = new Date(sale.timestamp);
        const formattedDate = saleDate.toISOString().split("T")[0]; // Format date as 'YYYY-MM-DD'
        
        if (salesByDate[formattedDate]) {
          salesByDate[formattedDate] += sale.totalAmount;
        } else {
          salesByDate[formattedDate] = sale.totalAmount;
        }
      });

      // Convert the salesByDate object to arrays for charting
      const dates = Object.keys(salesByDate);
      const amounts = Object.values(salesByDate);
      setSalesHistory({ dates, amounts });
    } else {
      console.error("No sales data found in database.");
    }
  };

  // Fetch the logged-in user's username
  const fetchUser = () => {
    const auth = getAuth(app); // Get Firebase authentication instance
    onAuthStateChanged(auth, (user) => {
      if (user) {
        setUsername(user.displayName || user.email.split('@')[0]); // Set the username (use displayName if available or email prefix)
      } else {
        setUsername("Guest"); // Default if not logged in
      }
    });
  };

  useEffect(() => {
    fetchData();
    fetchSalesHistory(); // Fetch sales data for the history graph
    fetchUser(); // Call the function to get the username
  }, []);

  // Data for Sales History Line Chart
  const lineChartData = {
    labels: salesHistory.dates, // Dates for the X-axis
    datasets: [
      {
        label: "Total Sales per Day",
        data: salesHistory.amounts, // Total sales for each day
        borderColor: "rgba(75, 192, 192, 1)",
        backgroundColor: "rgba(75, 192, 192, 0.2)",
        borderWidth: 1,
        fill: true, // Enable the area fill below the line
      },
    ],
  };

  // Data for Bar Chart
  const barChartData = {
    labels: categories,
    datasets: [
      {
        label: "Quantity per Category",
        data: quantities,
        backgroundColor: "rgba(75, 192, 192, 0.2)",
        borderColor: "rgba(75, 192, 192, 1)",
        borderWidth: 1,
      },
    ],
  };

  // Data for Doughnut Chart
  const donutChartData = {
    labels: categories,
    datasets: [
      {
        label: "Quantity per Category",
        data: quantities,
        backgroundColor: [
          "rgba(75, 192, 192, 0.7)",
          "rgba(153, 102, 255, 0.7)",
          "rgba(255, 159, 64, 0.7)",
          "rgba(255, 99, 132, 0.7)",
          "rgba(54, 162, 235, 0.7)",
          "rgba(255, 205, 86, 0.7)",
        ],
        borderColor: [
          "rgba(75, 192, 192, 1)",
          "rgba(153, 102, 255, 1)",
          "rgba(255, 159, 64, 1)",
          "rgba(255, 99, 132, 1)",
          "rgba(54, 162, 235, 1)",
          "rgba(255, 205, 86, 1)",
        ],
        borderWidth: 1,
      },
    ],
  };

  // Options for Doughnut chart
  const donutChartOptions = {
    responsive: true,
    plugins: {
      tooltip: {
        callbacks: {
          label: (tooltipItem) => {
            return `${tooltipItem.label}: ${tooltipItem.raw}`;
          },
        },
      },
    },
    cutoutPercentage: 50, // Makes it a donut chart
  };

  return (
    <div>
            {/* Low Stock Warning */}
            {lowStockCategories.length > 0 && (
        <div
            className="alert alert-warning"
            role="alert"
            style={{ marginBottom: "20px", fontSize: "16px" }}
        >
            <strong>Warning:</strong> The following items have low stock:
            <ul>
            {lowStockCategories.map((item, index) => (
                <li key={index}>
                {item.itemName} (Category: {item.category}, Quantity: {item.quantity})
                </li>
            ))}
            </ul>
        </div>
      )}

      <br></br>
      {/* Welcome message */}
      <div
        className="alert alert-dismissible alert-light shadow p-3"
        style={{
          maxWidth: "900px",
          textAlign: "left",
          margin: "0 auto",
          display: "block",
          backgroundColor: "#f8f9fa", // Light background color
        }}
      >
        Hello {username}
      </div>
      <br></br>
      {/* Welcome message */}
      <div
        className="alert alert-dismissible alert-light shadow p-3"
        style={{
          maxWidth: "900px",
          textAlign: "left",
          margin: "0 auto",
          display: "block",
          backgroundColor: "#f8f9fa", // Light background color
        }}
      >
      <button 
        type="button" 
        className="btn btn-secondary" 
        style={{ marginRight: "10px" }} 
        onClick={() => navigate("/inventory")}
      >
        <img src="images/inventory.png" style={{ width: "40px", height: "40px" }} />
        <br />
        <span style={{ fontSize: "12px" }}>Inventory</span>
      </button>

      <button 
        type="button" 
        className="btn btn-secondary" 
        style={{ marginRight: "10px" }} 
        onClick={() => navigate("/pos")}
      >
        <img src="images/pos.png" style={{ width: "40px", height: "40px" }} />
        <br />
        <span style={{ fontSize: "12px" }}>POS</span>
      </button>

      <button 
        type="button" 
        className="btn btn-secondary" 
        style={{ marginRight: "10px" }} 
        onClick={() => navigate("/saleshistory")}
      >
        <img src="images/sales.png" style={{ width: "40px", height: "40px" }} />
        <br />
        <span style={{ fontSize: "12px" }}>Sales</span>
      </button>

      </div>
      <br></br>
      {/* Card Container for Sales History */}
      <div className="card shadow p-3" style={{ width: "100%", maxWidth: "900px", margin: "0 auto" }}>
        <div className="card-header">
          <h5>Sales History</h5>
          <p>The Line Chart displays the total sales per day. Use this to track daily performance.</p>
        </div>
        <div className="card-body">
          {/* Line Chart for Sales History */}
          <div style={{ width: "100%", maxWidth: "100%", marginBottom: "30px" }}>
            <Line data={lineChartData} options={{ responsive: true, maintainAspectRatio: false }} />
          </div>
        </div>
      </div>

      <br></br>

      {/* Card for Quantity per Category */}
      <div className="card" style={{ width: "100%", maxWidth: "900px", margin: "0 auto" }}>
        <div className="card-header">
          <h5>Quantity of Items per Category</h5>
          <p>The Bar Chart displays item quantities by category, while the Doughnut Chart shows their proportions.</p>
        </div>
        <div className="card-body">
          {/* Bar Chart */}
          <div style={{ width: "100%", maxWidth: "100%", marginBottom: "30px" }}>
            <Bar data={barChartData} options={{ responsive: true, maintainAspectRatio: false }} />
          </div>
          <center>
            {/* Doughnut Chart */}
            <div style={{ width: "100%", maxWidth: "100%" }}>
              <Doughnut data={donutChartData} options={donutChartOptions} />
            </div>
          </center>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
